/**
 * ActivityHeatmapReport (FEAT-ANALYTICS) — activity by day-of-week × hour.
 * Answers "when is my audience active / best time to send" — a 7×24 heatmap with
 * a peak callout and an Events/Users metric toggle. Scoped by the toolbar window,
 * platform and audience filter.
 */
import React, { useMemo, useState } from 'react';
import { Box, Flex, Text, Select, HStack, Stack } from '@chakra-ui/react';
import { useReport } from './api';
import { Panel, StatCard, StatGrid, ReportFrame, fmtInt } from './components';

const HOURS = [...Array(24).keys()];
const hourLabel = (h) => (h === 0 ? '12a' : h < 12 ? `${h}a` : h === 12 ? '12p' : `${h - 12}p`);
// Full, readable hour for the KPI callouts (e.g. "3:00 pm"); the compact hourLabel
// stays on the space-constrained axis + tooltips.
const hourLabelFull = (h) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? 'am' : 'pm'}`;

function cellColor(v, max) {
  if (!v || !max) return '#f1f3f5';
  const a = 0.1 + 0.9 * (v / max);
  return `rgba(37,99,235,${a.toFixed(3)})`;
}

export function ActivityHeatmapReport({ params }) {
  const [metric, setMetric] = useState('events');
  const reportParams = useMemo(() => ({ ...params, metric }), [params, metric]);
  const { data, loading, error } = useReport('activity', reportParams);

  return (
    <Box>
      <Flex mb={4} align="center" justify="space-between" wrap="wrap" gap={2}>
        <HStack>
          <Text fontSize="sm" color="gray.600">Measure</Text>
          <Select size="sm" w="auto" bg="white" value={metric} onChange={(e) => setMetric(e.target.value)}>
            <option value="events">Events</option>
            <option value="users">Active users</option>
          </Select>
        </HStack>
      </Flex>

      <ReportFrame loading={loading} error={error}>
        {data && (
          <Stack spacing={4}>
            <StatGrid columns={{ base: 1, md: 3 }}>
              <StatCard
                label="Peak activity"
                value={data.peak?.value ? `${data.days[data.peak.day]} ${hourLabelFull(data.peak.hour)}` : '—'}
                help={data.peak?.value ? `${fmtInt(data.peak.value)} ${metric === 'users' ? 'users' : 'events'}` : undefined}
              />
              <StatCard label="Busiest day" value={busiest(data.days, data.dayTotals)} />
              <StatCard label="Busiest hour" value={busiestHour(data.hourTotals)} />
            </StatGrid>

            <Panel title={`Activity by day & hour — ${metric === 'users' ? 'active users' : 'events'}`}>
              <Text fontSize="xs" color="gray.500" mb={3}>Darker = more activity. Times are the event's recorded local time.</Text>
              <Box overflowX="auto">
                <Box minW="760px">
                  {/* hour axis */}
                  <Flex pl="42px" mb={1}>
                    {HOURS.map((h) => (
                      <Box key={h} flex="1" textAlign="center" fontSize="9px" color="gray.400">{h % 3 === 0 ? hourLabel(h) : ''}</Box>
                    ))}
                  </Flex>
                  {data.grid.map((row, di) => {
                    const rowMax = data.max || 1;
                    return (
                      <Flex key={di} align="center" mb="2px">
                        <Box w="42px" fontSize="11px" fontWeight="600" color="gray.600">{data.days[di]}</Box>
                        {row.map((v, h) => (
                          <Box
                            key={h}
                            flex="1"
                            h="22px"
                            mx="1px"
                            borderRadius="3px"
                            bg={cellColor(v, rowMax)}
                            title={`${data.days[di]} ${hourLabel(h)} — ${fmtInt(v)} ${metric === 'users' ? 'users' : 'events'}`}
                          />
                        ))}
                      </Flex>
                    );
                  })}
                </Box>
              </Box>
            </Panel>
          </Stack>
        )}
      </ReportFrame>
    </Box>
  );
}

function busiest(days, totals = []) {
  if (!totals.length) return '—';
  let i = 0;
  totals.forEach((t, j) => { if (t > totals[i]) i = j; });
  return days[i];
}
function busiestHour(totals = []) {
  if (!totals.length) return '—';
  let i = 0;
  totals.forEach((t, j) => { if (t > totals[i]) i = j; });
  return hourLabelFull(i);
}

export default ActivityHeatmapReport;
